// Pre-upload gate. Confirms the customer owns / has rights to the artwork,
// and surfaces low-resolution warnings before the file lands on the canvas.
// Consent is remembered per browser session so the customer isn't nagged on
// every upload — but warnings (if any) re-trigger the modal even after.
import { useEffect, useState } from 'react'

export interface PendingUpload {
  file: File
  dataUrl: string
  naturalWidth: number
  naturalHeight: number
}

interface Props {
  open: boolean
  pending: PendingUpload | null
  warnings: string[]
  onConfirm: () => void
  onCancel: () => void
}

export default function UploadConsentModal({ open, pending, warnings, onConfirm, onCancel }: Props) {
  const [agreed, setAgreed] = useState(false)
  useEffect(() => { if (open) setAgreed(false) }, [open, pending])

  if (!open || !pending) return null
  const { file, naturalWidth, naturalHeight } = pending
  const kb = Math.round(file.size / 1024)

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white rounded-lg max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="eyebrow">Before we upload</div>
        <h3 className="font-heading uppercase font-bold text-xl text-ink">Confirm artwork rights</h3>
        <div className="mt-3 text-xs text-ink-3 font-mono truncate">{file.name}</div>
        <div className="text-xs text-ink-3 mt-1">{naturalWidth} × {naturalHeight} px · {kb} KB</div>

        {warnings.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded p-3 mt-4">
            <div className="text-[10px] uppercase tracking-wider font-bold text-amber-800 mb-1">Heads up</div>
            <ul className="text-xs text-amber-900 list-disc pl-4 space-y-1">
              {warnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          </div>
        )}

        <label className="flex items-start gap-2 mt-4 text-sm text-ink-2 cursor-pointer">
          <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-1 flex-shrink-0" />
          <span>I confirm I own or have full permission to use this artwork. I take full responsibility for any third-party rights claims that may arise from printing it.</span>
        </label>

        <div className="flex gap-2 mt-5">
          <button onClick={onCancel} className="btn-outline flex-1 justify-center">Cancel</button>
          <button onClick={() => agreed && onConfirm()} disabled={!agreed} className="btn-gold flex-1 justify-center">Confirm and upload</button>
        </div>
      </div>
    </div>
  )
}

// Compute size/resolution warnings for a pending upload. Returns an empty
// array when the file passes (decent size for printing).
export function computeUploadWarnings(p: PendingUpload): string[] {
  const out: string[] = []
  const { naturalWidth: w, naturalHeight: h } = p
  if (w < 500 || h < 500) {
    out.push(`This image is very small (${w} × ${h} px). It will likely print blurry. We recommend a minimum of 1000 × 1000 px.`)
  } else if (w < 1000 || h < 1000) {
    out.push(`This image is ${w} × ${h} px. We recommend 1000 × 1000 px or larger for crisp prints at 300 DPI.`)
  }
  return out
}

// Session-level consent. Once the customer has confirmed once, we skip the
// modal for subsequent CLEAN uploads in the same browser session. Uploads
// that produce warnings still re-trigger the modal so the user has to
// re-ack the issues.
const CONSENT_KEY = 'evrst-upload-consent'
export function hasSessionConsent(): boolean {
  try { return sessionStorage.getItem(CONSENT_KEY) === 'yes' } catch { return false }
}
export function rememberSessionConsent(): void {
  try { sessionStorage.setItem(CONSENT_KEY, 'yes') } catch { /* private mode */ }
}
